import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loading, AccessDenied } from "./components/ui";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import EmployeeForm from "./pages/EmployeeForm";
import OrgChart from "./pages/OrgChart";
import Leave from "./pages/Leave";
import Attendance from "./pages/Attendance";
import Holidays from "./pages/Holidays";
import Tasks from "./pages/Tasks";
import Announcements from "./pages/Announcements";
import Vibe from "./pages/Vibe";
import Admin from "./pages/Admin";
import Payroll from "./pages/Payroll";
import Expenses from "./pages/Expenses";
import Documents from "./pages/Documents";
import Recruitment from "./pages/Recruitment";
import Performance from "./pages/Performance";
import Reports from "./pages/Reports";
import Onboarding from "./pages/Onboarding";
import Exit from "./pages/Exit";
import Helpdesk from "./pages/Helpdesk";
import Assets from "./pages/Assets";
import Travel from "./pages/Travel";
import Surveys from "./pages/Surveys";
import Loans from "./pages/Loans";
import Learning from "./pages/Learning";
import Confirmation from "./pages/Confirmation";
import EmailOutbox from "./pages/EmailOutbox";
import Recognition from "./pages/Recognition";
import Pulse from "./pages/Pulse";
import Timesheets from "./pages/Timesheets";
import Compensation from "./pages/Compensation";
import Calendar from "./pages/Calendar";
import CompOff from "./pages/CompOff";
import Requisitions from "./pages/Requisitions";
import Policies from "./pages/Policies";
import Profile360 from "./pages/Profile360";
import Feedback from "./pages/Feedback";
import CustomFields from "./pages/CustomFields";
import Talent from "./pages/Talent";
import MyProfile from "./pages/MyProfile";
import Approvals from "./pages/Approvals";
import Flows from "./pages/Flows";
import Settings from "./pages/Settings";
import Lifecycle from "./pages/Lifecycle";
import Grievances from "./pages/Grievances";
import Benefits from "./pages/Benefits";
import Fleet from "./pages/Fleet";
import TaxCenter from "./pages/TaxCenter";
import PayInputs from "./pages/PayInputs";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-slate-50 dark:bg-slate-950"><Loading label="Authenticating" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

const P = (el) => <Protected>{el}</Protected>;

function RoleRoute({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-slate-50 dark:bg-slate-950"><Loading /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Layout>{roles.includes(user.role) ? children : <AccessDenied />}</Layout>;
}
const R = (roles, el) => <RoleRoute roles={roles}>{el}</RoleRoute>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={P(<Dashboard />)} />
          <Route path="/me" element={P(<MyProfile />)} />
          <Route path="/approvals" element={P(<Approvals />)} />
          <Route path="/flows" element={R(["SUPERADMIN", "HR_ADMIN"], <Flows />)} />
          <Route path="/settings" element={R(["SUPERADMIN", "HR_ADMIN"], <Settings />)} />
          <Route path="/employees" element={P(<Employees />)} />
          <Route path="/employees/new" element={P(<EmployeeForm />)} />
          <Route path="/employees/:id" element={P(<EmployeeDetail />)} />
          <Route path="/employees/:id/edit" element={P(<EmployeeForm />)} />
          <Route path="/orgchart" element={P(<OrgChart />)} />
          <Route path="/leave" element={P(<Leave />)} />
          <Route path="/attendance" element={P(<Attendance />)} />
          <Route path="/holidays" element={P(<Holidays />)} />
          <Route path="/tasks" element={P(<Tasks />)} />
          <Route path="/announcements" element={P(<Announcements />)} />
          <Route path="/vibe" element={P(<Vibe />)} />
          <Route path="/recognition" element={P(<Recognition />)} />
          <Route path="/pulse" element={P(<Pulse />)} />
          <Route path="/timesheets" element={P(<Timesheets />)} />
          <Route path="/compoff" element={P(<CompOff />)} />
          <Route path="/calendar" element={P(<Calendar />)} />
          <Route path="/policies" element={P(<Policies />)} />
          <Route path="/my-profile" element={P(<Profile360 />)} />
          <Route path="/feedback" element={P(<Feedback />)} />
          <Route path="/requisitions" element={R(["SUPERADMIN", "HR_ADMIN", "MANAGER"], <Requisitions />)} />
          <Route path="/custom-fields" element={R(["SUPERADMIN", "HR_ADMIN"], <CustomFields />)} />
          <Route path="/admin" element={R(["SUPERADMIN"], <Admin />)} />
          <Route path="/payroll" element={R(["SUPERADMIN", "HR_ADMIN"], <Payroll />)} />
          <Route path="/expenses" element={P(<Expenses />)} />
          <Route path="/documents" element={P(<Documents />)} />
          <Route path="/recruitment" element={P(<Recruitment />)} />
          <Route path="/performance" element={P(<Performance />)} />
          <Route path="/reports" element={P(<Reports />)} />
          <Route path="/email-outbox" element={R(["SUPERADMIN", "HR_ADMIN"], <EmailOutbox />)} />
          <Route path="/onboarding" element={P(<Onboarding />)} />
          <Route path="/confirmation" element={R(["SUPERADMIN", "HR_ADMIN", "MANAGER"], <Confirmation />)} />
          <Route path="/exit" element={P(<Exit />)} />
          <Route path="/helpdesk" element={P(<Helpdesk />)} />
          <Route path="/assets" element={P(<Assets />)} />
          <Route path="/travel" element={P(<Travel />)} />
          <Route path="/surveys" element={P(<Surveys />)} />
          <Route path="/loans" element={P(<Loans />)} />
          <Route path="/compensation" element={R(["SUPERADMIN", "HR_ADMIN"], <Compensation />)} />
          <Route path="/learning" element={P(<Learning />)} />
          <Route path="/talent" element={P(<Talent />)} />
          <Route path="/lifecycle" element={P(<Lifecycle />)} />
          <Route path="/grievances" element={P(<Grievances />)} />
          <Route path="/benefits" element={P(<Benefits />)} />
          <Route path="/fleet" element={P(<Fleet />)} />
          <Route path="/tax" element={P(<TaxCenter />)} />
          <Route path="/pay-inputs" element={R(["SUPERADMIN", "HR_ADMIN"], <PayInputs />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
